import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-scroll';
import { FaBars, FaTimes } from 'react-icons/fa';

interface HeaderContainerProps {
  $scrolled: boolean;
}

const HeaderContainer = styled.header.attrs({
  as: motion.header
})`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  padding: 1rem 0;
  transition: all 0.3s ease;
  background-color: ${({ $scrolled }: { $scrolled: boolean }) => 
    $scrolled ? 'rgba(0, 0, 0, 0.9)' : 'transparent'};
  box-shadow: ${({ $scrolled }: { $scrolled: boolean }) => 
    $scrolled ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none'};
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Logo = styled.div.attrs({
  as: motion.div
})`
  font-family: var(--font-primary);
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--color-accent);
  cursor: pointer;

  span {
    color: var(--color-text);
  }
`;

interface NavProps {
  isOpen: boolean;
}

const Nav = styled.nav`
  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    right: ${({ isOpen }: { isOpen: boolean }) => (isOpen ? '0' : '-100%')};
    width: 70%;
    height: 100vh;
    background-color: var(--color-secondary);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    transition: right 0.3s ease-in-out;
    box-shadow: -5px 0 15px rgba(0, 0, 0, 0.2);
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const NavItem = styled.li`
  margin: 0 1rem;

  @media (max-width: 768px) {
    margin: 1rem 0;
  }
`;

const NavLink = styled.a.attrs({
  as: Link
})`
  font-family: var(--font-secondary);
  color: var(--color-text);
  font-size: 1rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background-color: var(--color-accent);
    transition: width 0.3s ease;
  }

  &:hover, &.active {
    color: var(--color-accent);

    &:after {
      width: 100%;
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: var(--color-text);
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 1001;

  @media (max-width: 768px) {
    display: block;
  }
`;

const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  return (
    <HeaderContainer
      $scrolled={scrolled}
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <HeaderContent>
        <Logo
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          My <span>Dream</span> Suits
        </Logo>

        <MobileMenuButton onClick={toggleMenu}>
          {isOpen ? <FaTimes /> : <FaBars />}
        </MobileMenuButton>

        <Nav isOpen={isOpen}>
          <NavList>
            <NavItem>
              <NavLink
                to="hero"
                smooth={true}
                duration={500}
                spy={true}
                exact="true"
                offset={-70}
                onClick={closeMenu}
              >
                Início
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                to="about"
                smooth={true}
                duration={500}
                spy={true}
                exact="true"
                offset={-70}
                onClick={closeMenu}
              >
                Sobre
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                to="services"
                smooth={true}
                duration={500}
                spy={true}
                exact="true"
                offset={-70}
                onClick={closeMenu}
              >
                Serviços
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                to="products"
                smooth={true}
                duration={500}
                spy={true}
                exact="true"
                offset={-70}
                onClick={closeMenu}
              >
                Produtos
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                to="gallery"
                smooth={true}
                duration={500}
                spy={true}
                exact="true"
                offset={-70}
                onClick={closeMenu}
              >
                Galeria
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                to="contact"
                smooth={true}
                duration={500}
                spy={true}
                exact="true"
                offset={-70}
                onClick={closeMenu}
              >
                Contato
              </NavLink>
            </NavItem>
          </NavList>
        </Nav>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header; 